import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useUserRole } from "@/hooks/useUserRole";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { ArrowLeft, FileText, Shield, FileCheck, Building2, Users, AlertTriangle, Flag } from "lucide-react";
import { BusinessDocumentManager } from "@/components/business/BusinessDocumentManager";
import { DPATracker } from "@/components/business/DPATracker";
import { ComplianceTracker } from "@/components/business/ComplianceTracker";
import { InsuranceManager } from "@/components/business/InsuranceManager";
import { StaffPermissionsManager } from "@/components/business/StaffPermissionsManager";
import { SafetyInvestigationsManager } from "@/components/business/SafetyInvestigationsManager";
import { ComplaintsManager } from "@/components/business/ComplaintsManager";
import { LaunchReadinessChecklist } from "@/components/business/LaunchReadinessChecklist";
import { ComplaintsSafetyExplainer } from "@/components/business/ComplaintsSafetyExplainer";

export default function BusinessPortal() {
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAccess = async () => {
      try {
        // Get user
        const { data: { user } } = await supabase.auth.getUser();
        
        if (!user) {
          navigate("/auth");
          return;
        }

        setUser(user);

        // Check admin role directly
        const { data: roles, error } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', user.id)
          .eq('role', 'admin')
          .maybeSingle();

        if (error) {
          console.error('[BusinessPortal] Error checking role:', error);
        }

        const hasAdminRole = !!roles;
        setIsAdmin(hasAdminRole);

        if (!hasAdminRole) {
          navigate("/dashboard");
        }
      } catch (error) {
        console.error('[BusinessPortal] Access check failed:', error);
        navigate("/dashboard");
      } finally {
        setLoading(false);
      }
    };

    checkAccess();
  }, [navigate]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b bg-card">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/dashboard")}
              className="gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Dashboard
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-foreground">Business Portal</h1>
              <p className="text-sm text-muted-foreground">
                Legal, Compliance & Business Management
              </p>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        <Tabs defaultValue="launch" className="space-y-6">
          <TabsList className="flex w-full overflow-x-auto scrollbar-hide gap-1 p-1.5 h-auto justify-start lg:justify-center">
            <TabsTrigger value="launch" className="gap-2">
              <AlertTriangle className="h-4 w-4" />
              Launch Readiness
            </TabsTrigger>
            <TabsTrigger value="complaints" className="gap-2">
              <Flag className="h-4 w-4" />
              Complaints
            </TabsTrigger>
            <TabsTrigger value="documents" className="gap-2">
              <FileText className="h-4 w-4" />
              Documents
            </TabsTrigger>
            <TabsTrigger value="dpa" className="gap-2">
              <FileCheck className="h-4 w-4" />
              DPA Tracking
            </TabsTrigger>
            <TabsTrigger value="compliance" className="gap-2">
              <Shield className="h-4 w-4" />
              Compliance
            </TabsTrigger>
            <TabsTrigger value="insurance" className="gap-2">
              <Building2 className="h-4 w-4" />
              Insurance
            </TabsTrigger>
            <TabsTrigger value="staff" className="gap-2">
              <Users className="h-4 w-4" />
              Staff Access
            </TabsTrigger>
            <TabsTrigger value="safety" className="gap-2">
              <Flag className="h-4 w-4" />
              Safety & Investigations
            </TabsTrigger>
          </TabsList>

          <TabsContent value="launch">
            <LaunchReadinessChecklist />
          </TabsContent>

          <TabsContent value="complaints" className="space-y-6">
            <ComplaintsSafetyExplainer />
            <ComplaintsManager />
          </TabsContent>

          <TabsContent value="documents">
            <BusinessDocumentManager />
          </TabsContent>

          <TabsContent value="dpa">
            <DPATracker />
          </TabsContent>

          <TabsContent value="compliance">
            <ComplianceTracker />
          </TabsContent>

          <TabsContent value="insurance">
            <InsuranceManager />
          </TabsContent>

          <TabsContent value="staff">
            <StaffPermissionsManager />
          </TabsContent>

          <TabsContent value="safety">
            <SafetyInvestigationsManager />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
